import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme, focusRing } from '../styles/theme';

// ─── BUTTON ───────────────────────────────────────────────────────────────────

const buttonVariants = {
  primary: css`
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    border: 1px solid transparent;
    &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; transform: translateY(-1px); box-shadow: ${theme.shadows.md}; }
  `,
  secondary: css`
    background: ${theme.colors.white};
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.border};
    &:hover:not(:disabled) { background: ${theme.colors.gray50}; border-color: ${theme.colors.borderHover}; }
  `,
  ghost: css`
    background: transparent;
    color: ${theme.colors.textSecondary};
    border: 1px solid transparent;
    &:hover:not(:disabled) { background: ${theme.colors.gray100}; color: ${theme.colors.textPrimary}; }
  `,
  ghostLight: css`
    background: transparent;
    color: rgb(255 255 255 / 0.85);
    border: 1px solid transparent;
    &:hover:not(:disabled) { background: rgb(255 255 255 / 0.12); color: ${theme.colors.white}; }
  `,
  danger: css`
    background: ${theme.colors.danger};
    color: ${theme.colors.white};
    border: 1px solid transparent;
    &:hover:not(:disabled) { background: #dc2626; transform: translateY(-1px); }
  `,
  success: css`
    background: ${theme.colors.success};
    color: ${theme.colors.white};
    border: 1px solid transparent;
    &:hover:not(:disabled) { background: #059669; transform: translateY(-1px); }
  `,
};

const buttonSizes = {
  sm: css`padding: ${theme.spacing[2]} ${theme.spacing[3]}; font-size: ${theme.typography.xs}; gap: ${theme.spacing[1]};`,
  md: css`padding: ${theme.spacing[2]} ${theme.spacing[4]}; font-size: ${theme.typography.sm}; gap: ${theme.spacing[2]};`,
  lg: css`padding: ${theme.spacing[3]} ${theme.spacing[6]}; font-size: ${theme.typography.base}; gap: ${theme.spacing[2]};`,
};

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.medium};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  white-space: nowrap;
  outline: none;
  ${({ variant = 'primary' }) => buttonVariants[variant]}
  ${({ size = 'md' }) => buttonSizes[size]}
  &:focus-visible { box-shadow: 0 0 0 3px ${theme.colors.primaryLight}, 0 0 0 1px ${theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
  &:active:not(:disabled) { transform: translateY(0); }
`;

// ─── CARD ─────────────────────────────────────────────────────────────────────

export const Card = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.sm};
  ${({ padding }) => padding !== false && css`padding: ${theme.spacing[6]};`}
  ${({ hoverable }) => hoverable && css`
    cursor: pointer;
    transition: ${theme.transitions.base};
    &:hover { box-shadow: ${theme.shadows.lg}; transform: translateY(-2px); border-color: ${theme.colors.borderHover}; }
    &:active { transform: translateY(0); }
    ${focusRing}
  `}
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[4]};
  gap: ${theme.spacing[3]};
`;

export const CardTitle = styled.h3`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

// ─── INPUT ────────────────────────────────────────────────────────────────────

export const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  outline: none;
  &::placeholder { color: ${theme.colors.textMuted}; }
  &:hover { border-color: ${theme.colors.borderHover}; }
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
  &:disabled { background: ${theme.colors.gray100}; cursor: not-allowed; opacity: 0.7; }
`;

// ─── PASSWORD INPUT (com toggle de visibilidade) ────────────────────────────────

const PasswordWrap = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button.attrs({ type: 'button' })`
  position: absolute;
  right: ${theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[1]};
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  border-radius: ${theme.radii.sm};
  transition: ${theme.transitions.fast};
  &:hover { color: ${theme.colors.textSecondary}; }
  &:focus-visible { box-shadow: 0 0 0 2px ${theme.colors.primaryLight}; outline: none; }
`;

/**
 * Input de senha com botão de olho para alternar a visibilidade do conteúdo.
 * Drop-in para campos type="password": aceita as mesmas props do Input.
 * O botão só aparece quando type === 'password'; para outros tipos, renderiza
 * um Input normal. `as` permite reaproveitar componentes de input estilizados
 * (ex: ModalInput) mantendo o toggle.
 */
export const PasswordInput = ({ as: AsComp = Input, type = 'password', style, ...rest }) => {
  const { t } = useTranslation('common');
  const [show, setShow] = useState(false);
  if (type !== 'password') {
    return <AsComp type={type} style={style} {...rest} />;
  }
  const toggleLabel = show
    ? t('hidePassword', { defaultValue: 'Ocultar' })
    : t('showPassword', { defaultValue: 'Mostrar' });
  return (
    <PasswordWrap style={style}>
      <AsComp
        type={show ? 'text' : 'password'}
        style={{ paddingRight: '40px', width: '100%' }}
        {...rest}
      />
      <PasswordToggle
        onClick={() => setShow((v) => !v)}
        aria-label={toggleLabel}
        title={toggleLabel}
        tabIndex={-1}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </PasswordToggle>
    </PasswordWrap>
  );
};

export const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  outline: none;
  cursor: pointer;
  &:hover { border-color: ${theme.colors.borderHover}; }
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

export const Label = styled.label`
  display: block;
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[1]};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

// ─── BADGE ────────────────────────────────────────────────────────────────────

const badgeVariants = {
  success: css`background: ${theme.colors.successLight}; color: #065f46; border-color: ${theme.colors.successBorder};`,
  warning: css`background: ${theme.colors.warningLight}; color: #92400e; border-color: ${theme.colors.warningBorder};`,
  danger:  css`background: ${theme.colors.dangerLight};  color: #991b1b; border-color: ${theme.colors.dangerBorder};`,
  info:    css`background: ${theme.colors.infoLight};    color: #1a49ad; border-color: ${theme.colors.infoBorder};`,
  primary: css`background: ${theme.colors.primaryLight}; color: #153d94; border-color: ${theme.colors.primaryBorder};`,
  neutral: css`background: ${theme.colors.gray100};      color: ${theme.colors.gray700}; border-color: ${theme.colors.gray200};`,
};

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: 2px ${theme.spacing[2]};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  border-radius: ${theme.radii.full};
  border: 1px solid transparent;
  white-space: nowrap;
  ${({ variant = 'neutral' }) => badgeVariants[variant]}
`;

// ─── MODAL ────────────────────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing[4]};
  animation: fadeIn 150ms ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @media (max-width: 639px) {
    align-items: flex-end;
    padding: 0;
  }
`;

export const ModalBox = styled.div`
  background: ${theme.colors.bgCard};
  border-radius: ${theme.radii['2xl']};
  box-shadow: ${theme.shadows.xl};
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || '520px'};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 150ms ease;
  @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @media (max-width: 639px) {
    max-height: 92vh;
    border-radius: ${theme.radii['2xl']} ${theme.radii['2xl']} 0 0;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  @media (max-width: 639px) {
    padding: ${theme.spacing[4]};
  }
`;

export const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

export const ModalBody = styled.div`
  padding: ${theme.spacing[6]};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  @media (max-width: 639px) {
    padding: ${theme.spacing[4]};
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
  @media (max-width: 639px) {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    flex-direction: column-reverse;
    & > * { width: 100%; justify-content: center; }
  }
`;

// ─── SPINNER ──────────────────────────────────────────────────────────────────

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

export const Spinner = styled.div`
  width: ${({ size }) => size || '20px'};
  height: ${({ size }) => size || '20px'};
  border: 2px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

// ─── SKELETON ─────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

export const Skeleton = styled.div`
  background: linear-gradient(90deg,
    ${theme.colors.gray100} 25%,
    ${theme.colors.gray50}  50%,
    ${theme.colors.gray100} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: ${({ radius }) => radius || theme.radii.md};
  width:  ${({ width })  => width  || '100%'};
  height: ${({ height }) => height || '16px'};
  flex-shrink: 0;
`;

export function SkeletonRow({ avatar = false, lines = 2 }) {
  return (
    <SkeletonRowWrap>
      {avatar && <Skeleton width="40px" height="40px" radius="50%" style={{ flexShrink: 0 }} />}
      <SkeletonRowLines>
        <Skeleton height="13px" width="60%" />
        {lines >= 2 && <Skeleton height="11px" width="40%" />}
        {lines >= 3 && <Skeleton height="11px" width="50%" />}
      </SkeletonRowLines>
    </SkeletonRowWrap>
  );
}

const SkeletonRowWrap = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
`;
const SkeletonRowLines = styled.div`
  flex: 1; display: flex; flex-direction: column; gap: ${theme.spacing[2]};
`;


// ─── PAGE LAYOUT ──────────────────────────────────────────────────────────────

export const PageContainer = styled.div`
  padding: ${theme.spacing[6]};
  background: ${theme.colors.bg};
  min-height: calc(100vh - 64px);
  transition: margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  @media (max-width: 768px) {
    padding: ${theme.spacing[4]} ${theme.spacing[3]};
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[6]};
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
  @media (max-width: 639px) {
    margin-bottom: ${theme.spacing[4]};
    gap: ${theme.spacing[2]};
  }
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography['2xl']};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  @media (max-width: 639px) {
    font-size: ${theme.typography.xl};
  }
`;

export const PageSubtitle = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0 0;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ min }) => min || '280px'}, 1fr));
  gap: ${({ gap }) => gap || theme.spacing[5]};
  @media (max-width: 639px) {
    grid-template-columns: 1fr;
  }
`;

export const Flex = styled.div`
  display: flex;
  align-items: ${({ align }) => align || 'center'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  gap: ${({ gap }) => gap || theme.spacing[3]};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
`;

export const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
  margin: ${({ my }) => my || `${theme.spacing[4]} 0`};
`;

// ─── STAT CARD ────────────────────────────────────────────────────────────────

export const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  border-left: 3px solid ${({ accent }) => accent || theme.colors.primary};
`;

export const StatValue = styled.div`
  font-size: ${theme.typography['3xl']};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
  line-height: ${theme.typography.tight};
  @media (max-width: 639px) {
    font-size: ${theme.typography['2xl']};
  }
`;

export const StatLabel = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
`;

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[16]} ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
  gap: ${theme.spacing[3]};
`;

export const EmptyIcon = styled.div`
  color: ${theme.colors.gray300};
  margin-bottom: ${theme.spacing[2]};
`;

export const EmptyText = styled.p`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

export const EmptyHint = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

// Componente completo com ação opcional
// Uso: <EmptyStateAction icon={<Bot size={48} />} title="Sem agentes" hint="Instale um agente" action={() => {}} actionLabel="Instalar" />
export function EmptyStateAction({ icon, title, hint, action, actionLabel, actionVariant = 'primary' }) {
  return (
    <EmptyState>
      {icon && <EmptyIcon>{icon}</EmptyIcon>}
      {title && <EmptyText>{title}</EmptyText>}
      {hint && <EmptyHint>{hint}</EmptyHint>}
      {action && actionLabel && (
        <Button variant={actionVariant} onClick={action} style={{ marginTop: theme.spacing[4] }}>
          {actionLabel}
        </Button>
      )}
    </EmptyState>
  );
}

// ─── LOADING ──────────────────────────────────────────────────────────────────

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[12]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm};
`;

// ─── SECTION TITLE ────────────────────────────────────────────────────────────
// Título de seção dentro de página. Use as="h3" para subseções.
export const SectionTitle = styled.h2`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[4]} 0;
`;

// ─── SECTION LABEL ────────────────────────────────────────────────────────────
// Rótulo uppercase de grupo (ex: "Configurações", "Fila de espera")
export const SectionLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: ${theme.spacing[2]};
`;

// ─── FIELD HINT ───────────────────────────────────────────────────────────────
// Texto auxiliar abaixo de inputs e selects
export const FieldHint = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  margin-top: 4px;
`;

// ─── TOGGLE ───────────────────────────────────────────────────────────────────
export const ToggleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
`;
export const ToggleLabel = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
`;
export const ToggleDesc = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.4;
`;
export const ToggleSwitch = styled.div`
  width: 40px; height: 22px;
  border-radius: 11px;
  background: ${p => p.$active ? theme.colors.primary : theme.colors.border};
  cursor: pointer; flex-shrink: 0; position: relative;
  transition: background 0.2s;
  margin-top: 2px;
`;
export const ToggleThumb = styled.div`
  width: 18px; height: 18px;
  border-radius: 50%;
  background: white;
  position: absolute; top: 2px;
  left: ${p => p.$active ? '20px' : '2px'};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;
